import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { Dayjs } from 'dayjs';
import type { SupportedCurrencies } from 'frontend-utils/utils/constants/currency-mapping-constants';
import type { GeneralDatepickersDatepickerWithSidebarSignature } from 'lumen/components/general/datepickers/datepicker-with-sidebar';
import type {
  MarketSnapshotsPageDates,
  MarketSnapshotsPageKpiSection,
  MarketSnapshotsPagePagination,
  MarketSnapshotsPageTable,
} from 'lumen/components/market/snapshots/page';
import type {
  Kpi,
  MetricType,
} from 'lumen/models/market-performance/data-item';
import type MarketPerformanceKpi from 'lumen/models/market-performance/kpi';
import type { MarketSnapshotsModel } from 'lumen/routes/market/snapshots/base';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import { LOCAL_STORAGE_KEYS } from 'lumen/services/local-storage';
import {
  type DataPreferences,
  DEFAULT_DATA_PREFERENCES,
  DEFAULT_TABLE_LAYOUT_PREFERENCES,
  GROUP_BY,
  GROUP_COLUMNS_BY,
  type GroupBy,
  isGroupBy,
  isShowAs,
  SHOW_AS,
  type ShowAs,
  type SnapshotViewConfig,
  type TableLayoutPreferences,
} from 'lumen/utils/constants/market-snapshots-constants';
import { formatMonthDateRange } from 'lumen/utils/dates/format-month-date-range';
import {
  type ColumnSettings,
  defaultColumnSettings,
  getVisibleKpis,
  getVisibleMetricTypes,
} from 'lumen/utils/market-snapshots/column-settings';
import {
  buildSnapshotSidebarLists,
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
} from 'lumen/utils/market-snapshots/defaults';
import { isSortedColumnHidden } from 'lumen/utils/market-snapshots/order-by';
import type { SnapshotItems } from 'lumen/utils/market-snapshots/query';

/**
 * Shared query params, date range, grouping, sorting, pagination and
 * reorderable/hideable columns for the market snapshots views. Views extend
 * this with their `config` and their own preference actions.
 */
export default abstract class MarketSnapshotsBaseController extends Controller {
  declare model: MarketSnapshotsModel;

  // Class fields are not concatenated across subclasses; views must not redeclare this.
  queryParams = [
    'fromDate',
    'toDate',
    'groupBy',
    'orderBy',
    'page',
    'pageSize',
    'showAs',
  ];

  // Not `dates`: that name is kept for the page shell's date args.
  @service('dates') declare datesService: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service declare intl: Services['intl'];
  @service('local-storage') declare localStorage: Services['local-storage'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];

  // Empty until the route's setupController reads the stored settings.
  @tracked columnSettings: ColumnSettings = defaultColumnSettings([]);
  @tracked dataPreferences: DataPreferences = DEFAULT_DATA_PREFERENCES;
  @tracked fromDate?: string;
  @tracked groupBy: GroupBy = GROUP_BY.PROPERTY;
  @tracked kpiColumnSettings: ColumnSettings<Kpi> = defaultColumnSettings([]);
  // Semantic sort: "field" ascending, "-field" descending (e.g. "otb.totals.revenue").
  // The route derives the API order_by from this plus sortOn/variance.
  @tracked orderBy = 'name';
  @tracked page = 1;
  @tracked pageSize = DEFAULT_PAGE_SIZE;
  @tracked showAs: ShowAs = SHOW_AS.TABLE;
  @tracked tableLayoutPreferences: TableLayoutPreferences =
    DEFAULT_TABLE_LAYOUT_PREFERENCES;
  @tracked toDate?: string;

  abstract readonly config: SnapshotViewConfig;
  pageSizeOptions = PAGE_SIZE_OPTIONS;

  get areKpisLoading(): boolean {
    return this.model.loadDataItemsTask.isRunning && !this.kpiCurrentLoad;
  }

  get columnMetrics(): Record<string, MetricType> {
    return this.config.columns.metrics(this.dataPreferences);
  }

  get currency(): SupportedCurrencies {
    return this.hotelAndMarketService.market.currencyCode;
  }

  get currentPageSizeOption(): { label: string; value: number } {
    return {
      value: this.pageSize,
      label: this.intl.t('general.page_no_of_rows', {
        noOfRows: this.intl.formatNumber(this.pageSize),
      }),
    };
  }

  get datepickerSidebarLists(): GeneralDatepickersDatepickerWithSidebarSignature['Args']['sidebarLists'] {
    return buildSnapshotSidebarLists(
      this.hotelAndMarketService.getLocalTime(),
      this.intl,
      this.groupBy,
    );
  }

  get dateRange(): { end: Dayjs; start: Dayjs } {
    const today = this.hotelAndMarketService.getLocalTime();

    return {
      start: this.fromDate
        ? this.hotelAndMarketService.toLocalTime(this.fromDate)
        : today.startOf('month'),
      end: this.toDate
        ? this.hotelAndMarketService.toLocalTime(this.toDate)
        : today.endOf('month'),
    };
  }

  // The page shell's grouped args. The actions in them are `@action`-bound.
  /* eslint-disable @typescript-eslint/unbound-method */
  get dates(): MarketSnapshotsPageDates {
    return {
      fromDate: this.fromDate,
      maxDate: this.maxDate,
      onChange: this.updateDateRange,
      range: this.dateRange,
      sidebarLists: this.datepickerSidebarLists,
      toDate: this.toDate,
      triggerLabelFormatter: this.isMonthView
        ? this.monthTriggerLabelFormatter
        : undefined,
    };
  }

  get isChartView(): boolean {
    return this.showAs === SHOW_AS.CHART;
  }

  get isMonthView(): boolean {
    return this.groupBy === GROUP_BY.MONTH;
  }

  // The last load, only while it matches the selected market, dates and compared
  // metrics: pagination and sorting keep the KPIs, any of those changes (or a
  // failed one) leaves them empty.
  get kpiCurrentLoad(): SnapshotItems | undefined {
    const result = this.model.loadDataItemsTask.lastSuccessful?.value;
    if (!result) {
      return undefined;
    }

    const { end, start } = this.dateRange;
    const { compare, dateRange, marketId } = result.params;
    const isSelectedLoad =
      marketId === this.hotelAndMarketService.market.id &&
      dateRange.from === start.format('YYYY-MM-DD') &&
      dateRange.to === end.format('YYYY-MM-DD') &&
      compare === this.config.api.compare(this.dataPreferences);

    return isSelectedLoad ? result : undefined;
  }

  get kpis(): MarketPerformanceKpi[] {
    return this.kpiCurrentLoad
      ? [...this.store.peekAll<MarketPerformanceKpi>('market-performance/kpi')]
      : [];
  }

  get kpiSection(): MarketSnapshotsPageKpiSection {
    return {
      comparisonMetricType: this.dataPreferences.sdlyColumn,
      currency: this.currency,
      isLoading: this.areKpisLoading,
      kpis: this.kpis,
      names: this.config.kpiCards,
      varianceType: this.dataPreferences.variance,
    };
  }

  get maxDate(): Dayjs {
    const today = this.hotelAndMarketService.getLocalTime();
    return today.add(2, 'year').endOf('year');
  }

  get pagination(): MarketSnapshotsPagePagination {
    return {
      lastPage:
        this.model.loadDataItemsTask.lastSuccessful?.value?.meta?.page_count ??
        1,
      onChangePageSize: this.onChangePageSize,
      onSetPage: this.setPage,
      page: this.page,
      pageSizeOptions: this.pageSizeOptions,
      pageSizeValue: this.currentPageSizeOption,
      showFooter: !this.isMonthView && (this.totalRecords ?? 0) > 0,
      showingText: this.showingText,
      totalRecords: this.totalRecords,
    };
  }

  get showingText(): string {
    const totalRecords = this.totalRecords ?? 0;

    if (totalRecords === 0) {
      return '';
    }

    const startIndex = (this.page - 1) * this.pageSize + 1;
    const endIndex = Math.min(this.page * this.pageSize, totalRecords);

    return this.intl.t('general.page_showing', {
      currentPage: this.intl.formatNumber(startIndex),
      lastPage: this.intl.formatNumber(endIndex),
      totalRecords: this.intl.formatNumber(totalRecords),
    });
  }

  get table(): MarketSnapshotsPageTable {
    // `last`, not `lastSuccessful`: the rows empty while a reload runs or after a failure.
    const lastResult = this.model.loadDataItemsTask.last?.value;

    return {
      groupBy: this.groupBy,
      groupColumnsBy: this.tableLayoutPreferences.groupColumnsBy,
      isLoading: this.model.loadDataItemsTask.isRunning,
      items: lastResult?.items ?? [],
      onSetGroupBy: this.setGroupBy,
      onSetShowAs: this.setShowAs,
      onSort: this.onSort,
      orderBy: this.orderBy,
      showAs: this.showAs,
      totals: lastResult?.meta?.totals,
      varianceType: this.dataPreferences.variance,
      visibleKpis: this.visibleKpis,
      visibleMetricTypes: this.visibleMetricTypes,
    };
  }
  /* eslint-enable @typescript-eslint/unbound-method */

  get totalRecords(): number | null {
    return (
      this.model.loadDataItemsTask.lastSuccessful?.value?.meta?.count ?? null
    );
  }

  // Only KPI mode applies the KPI settings; categories mode shows every KPI of the view.
  get visibleKpis(): readonly Kpi[] {
    return this.tableLayoutPreferences.groupColumnsBy === GROUP_COLUMNS_BY.KPIS
      ? getVisibleKpis(this.kpiColumnSettings)
      : this.config.columns.kpis;
  }

  get visibleMetricTypes(): MetricType[] {
    return getVisibleMetricTypes(
      this.columnSettings,
      this.tableLayoutPreferences,
      this.columnMetrics,
      this.config.columns.fixed,
    );
  }

  // Data and table-layout preferences are shared by every view (columns stay per view).

  /** Stores the data preferences under the shared key. */
  persistDataPreferences(preferences: DataPreferences): void {
    this.dataPreferences = preferences;
    this.localStorage.setItem(
      LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_DATA_PREFERENCES,
      JSON.stringify(preferences),
    );
  }

  /**
   * Stores the table layout under the shared key; a sortOn change resets the
   * sort (the orderBy/page QP change refetches).
   */
  persistTableLayoutPreferences(preferences: TableLayoutPreferences): void {
    const sortOnChanged =
      preferences.sortOn !== this.tableLayoutPreferences.sortOn;

    this.tableLayoutPreferences = preferences;
    this.localStorage.setItem(
      LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_TABLE_LAYOUT,
      JSON.stringify(preferences),
    );

    if (sortOnChanged) {
      this.onSort('name');
    }
  }

  /** Refetches from the first page when the request changed but the URL did not. */
  refetchFromFirstPage(): void {
    if (this.page !== 1) {
      // Resetting page (a refreshModel QP) refetches; router.refresh would abort it.
      this.page = 1;
    } else {
      this.refresh();
    }
  }

  /** Refetches this view when the request changed but the URL did not. */
  refresh(): void {
    this.router.refresh(`market.snapshots.${this.config.id}`);
  }

  @action
  monthTriggerLabelFormatter(start: Dayjs, end: Dayjs): string {
    return formatMonthDateRange(
      start,
      end,
      this.datesService.partialDateFormat.YEAR_AND_MONTH_LONG,
    );
  }

  @action
  onChangePageSize(pageSizeOption: { label: string; value: number }): void {
    this.pageSize = pageSizeOption.value;
    this.page = 1;
  }

  @action
  onSort(orderBy: string): void {
    this.orderBy = orderBy;
    this.page = 1;
  }

  @action
  setGroupBy(groupBy: string): void {
    // orderBy stays in the URL on purpose so switching back restores the sort.
    // Date snapping happens in the transition validator, not here.
    if (!isGroupBy(groupBy)) {
      return;
    }

    if (groupBy === GROUP_BY.MONTH && !this.isMonthView) {
      const today = this.hotelAndMarketService.getLocalTime();
      this.fromDate = today.startOf('year').format('YYYY-MM-DD');
      this.toDate = today.endOf('year').format('YYYY-MM-DD');
    }

    this.groupBy = groupBy;
    this.page = 1;
  }

  @action
  setPage(page: number): void {
    this.page = page;
  }

  @action
  setShowAs(showAs: string): void {
    if (!isShowAs(showAs)) {
      return;
    }

    this.showAs = showAs;
  }

  @action
  updateDateRange(range: { end: Dayjs | null; start: Dayjs }): void {
    this.fromDate = range.start.format('YYYY-MM-DD');
    this.toDate = (range.end ?? range.start).format('YYYY-MM-DD');
  }

  @action
  updateTableLayoutPreferences(
    preferences: TableLayoutPreferences,
    columns: { kpiSettings: ColumnSettings<Kpi>; settings: ColumnSettings },
  ): void {
    const { storageKeys } = this.config.columns;

    this.persistTableLayoutPreferences(preferences);

    this.columnSettings = columns.settings;
    this.localStorage.setItem(
      storageKeys.columns,
      JSON.stringify(columns.settings),
    );

    this.kpiColumnSettings = columns.kpiSettings;
    this.localStorage.setItem(
      storageKeys.kpiColumns,
      JSON.stringify(columns.kpiSettings),
    );

    // A sort on a now-hidden column would be left pointing at nothing visible;
    // reset it (the orderBy/page QP change refetches). A sortOn change already did.
    const sortedIsHidden = isSortedColumnHidden(this.orderBy, {
      isKpiMode: preferences.groupColumnsBy === GROUP_COLUMNS_BY.KPIS,
      visibleKpis: this.visibleKpis,
      visibleMetricTypes: this.visibleMetricTypes,
    });

    if (sortedIsHidden) {
      this.onSort('name');
    }
  }
}
